import React, { useMemo } from "react";
import { Avatar, Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getLicenseColor } from "../utils/drivers.util";

function getStandingsPoints(driver) {
  return driver?.current_points ?? driver?.points ?? 0;
}

export default function PodiumDisplay({ topDrivers = [], drivers = [] }) {
  const navigate = useNavigate();

  const driverById = useMemo(() => {
    const m = new Map();
    (drivers || []).forEach((driver) => m.set(driver.id, driver));
    return m;
  }, [drivers]);

  const podiumMeta = [
    { medal: getLicenseColor("gold"), label: "1", bg: "linear-gradient(135deg, #FFD70022 0%, transparent 100%)" },
    { medal: getLicenseColor("silver"), label: "2", bg: "linear-gradient(135deg, #C0C0C022 0%, transparent 100%)" },
    { medal: getLicenseColor("bronze"), label: "3", bg: "linear-gradient(135deg, #CD7F3222 0%, transparent 100%)" },
  ];

  if (topDrivers.length !== 3) return null;

  return (
    <Box sx={{ mb: { xs: 3, sm: 4 }, mt: { xs: 2, sm: 8 } }}>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          gap: 2,
          overflowX: "auto",
          pb: 1,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {topDrivers.map((driver, i) => {
          const meta = podiumMeta[i];
          const profile = driverById.get(driver.id) || {};

          return (
            <Paper
              key={`podium-xs-${driver.id}-${i}`}
              onClick={() => navigate(`/driver/${driver.id}`)}
              sx={{
                cursor: "pointer",
                minWidth: 260,
                flex: "0 0 auto",
                scrollSnapAlign: "start",
                p: 2,
                borderRadius: 3,
                background: meta.bg,
                border: `2px solid ${meta.medal}`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  src={profile.image}
                  alt={`${profile.name ?? driver.name} profile`}
                  sx={{
                    width: 56,
                    height: 56,
                    border: `3px solid ${meta.medal}`,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: meta.medal, fontWeight: 900, lineHeight: 1 }}>
                    #{meta.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }} noWrap>
                    {profile.name ?? driver?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {profile.team ?? ""}
                  </Typography>
                </Box>
                <Chip label={`${getStandingsPoints(driver)} pts`} color="primary" />
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="end"
        sx={{ display: { xs: "none", sm: "flex" } }}
      >
        {topDrivers.map((driver, i) => {
          const heights = [320, 280, 260];
          const paddingBottoms = ["127px", "81px", "64px"];
          const meta = podiumMeta[i];
          const profile = driverById.get(driver.id) || {};

          return (
            <Grid
              key={`podium-sm-${driver.id}-${i}`}
              item
              xs={12}
              sm={4}
              onClick={() => navigate(`/driver/${driver.id}`)}
              sx={{ cursor: "pointer" }}
            >
              <Paper
                sx={{
                  p: 2,
                  height: heights[i],
                  padding: "10px",
                  paddingBottom: paddingBottoms[i],
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: meta.bg,
                  border: `2px solid ${meta.medal}`,
                  borderRadius: 3,
                }}
              >
                <Avatar
                  src={profile.image}
                  alt={`${profile.name ?? driver?.name} profile`}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1.5,
                    border: `3px solid ${meta.medal}`,
                  }}
                />
                <Typography variant="h1" sx={{ color: meta.medal, fontWeight: 900 }}>
                  {meta.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {profile.name ?? driver?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.team ?? ""}
                </Typography>
                <Chip label={`${getStandingsPoints(driver)} pts`} color="primary" sx={{ mt: 1 }} />
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
